export class AccessibleNav {
  private readonly navSelector: string;
  private readonly desktopBreakpoint: number;
  private readonly alwaysShowToggle: boolean;
  private navs: HTMLElement[] = [];
  private readonly toggleSvgMarkup = `<svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_9028_971)">
      <path d="M5 6C4.67291 6 4.34582 5.8713 4.09716 5.61389L0.276804 1.66648C-0.0922679 1.28514 -0.0922679 0.667355 0.276804 0.286009C0.645876 -0.0953365 1.24377 -0.0953365 1.61284 0.286009L5 3.78581L8.38716 0.286009C8.75623 -0.0953365 9.35412 -0.0953365 9.7232 0.286009C10.0923 0.667355 10.0923 1.28514 9.7232 1.66648L5.90284 5.61389C5.65418 5.8713 5.32709 6 5 6Z" fill = "#DB0007" />
        </g>
    <defs>
      <clipPath id="clip0_9028_971">
        <rect width="10" height="6" fill="white" />
      </clipPath>
    </defs>
  </svg>`;

constructor(
  navSelector: string = '.navigation',
  desktopBreakpoint: number = 990,
  alwaysShowToggle: boolean = false
) {
  this.navSelector = navSelector;
  this.desktopBreakpoint = desktopBreakpoint;
  this.alwaysShowToggle = alwaysShowToggle;
  this.init();
}

  public refresh(): void {
  this.init();
}

  private init(): void {
  this.navs = Array.from(document.querySelectorAll(this.navSelector)) as HTMLElement[];
  if(!this.navs.length) return;

  this.navs.forEach((nav) => {
    if (nav.dataset.accessibleNavInitialized === 'true') return;

    nav.dataset.accessibleNavInitialized = 'true';
    this.prepareMenu(nav);
    this.bindEscapeToClose(nav);
    this.bindOutsideClickToClose(nav);
  });
}

  private prepareMenu(nav: HTMLElement): void {
  nav.classList.toggle('navigation--show-accessible-toggle', this.alwaysShowToggle);
  this.addCloseLinks(nav);

  const items = nav.querySelectorAll('li.has-children');
  items.forEach((item) => this.prepareMenuItem(item as HTMLElement));
}

  private prepareMenuItem(item: HTMLElement): void {
  const trigger = item.querySelector(':scope > a, :scope > .placeholder') as HTMLElement | null;
  const submenu = item.querySelector(':scope > ul') as HTMLElement | null;

  if(!trigger || !submenu) return;

if (!trigger.hasAttribute('aria-expanded')) {
  trigger.setAttribute('aria-expanded', 'false');
}

let toggler = item.querySelector(':scope > .toggler, :scope > .navigation__toggle') as HTMLButtonElement | null;
if (!toggler) {
  const labelText = (trigger.textContent || 'navigation item').trim();
  toggler = document.createElement('button');
  toggler.type = 'button';
  toggler.className = 'toggler navigation__toggle';
  toggler.setAttribute('aria-label', `Toggle child pages of ${labelText}`);
  toggler.setAttribute('aria-expanded', 'false');
  toggler.innerHTML = this.toggleSvgMarkup;
  trigger.innerHTML = trigger.innerHTML.trim() + ' ' + toggler.outerHTML;
} else {
  if (!toggler.classList.contains('toggler')) {
    toggler.classList.add('toggler');
  }

  if (!toggler.classList.contains('navigation__toggle')) {
    toggler.classList.add('navigation__toggle');
  }

  if (!toggler.innerHTML.trim()) {
    toggler.innerHTML = this.toggleSvgMarkup;
  }
}

if (toggler.dataset.accessibleNavBound !== 'true') {
  toggler.dataset.accessibleNavBound = 'true';

  toggler.addEventListener('click', (e) => {
    e.stopPropagation();
    this.toggleItem(item, trigger, toggler!, submenu);
  });

  toggler.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggleItem(item, trigger, toggler!, submenu);
    }
  });
}

if (trigger.classList.contains('placeholder') && trigger.dataset.accessibleNavBound !== 'true') {
  trigger.dataset.accessibleNavBound = 'true';

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    this.toggleItem(item, trigger, toggler!, submenu);
  });

  trigger.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggleItem(item, trigger, toggler!, submenu);
    }
  });
}
  }

  private addCloseLinks(nav: HTMLElement): void {
  const level2Menus = nav.querySelectorAll('.nav-level2');
  level2Menus.forEach((menu) => {
    if (menu.querySelector(':scope > .close-nav')) return;

    const closeContainer = document.createElement('div');
    closeContainer.className = 'close-nav';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'nav-level2__link close-nav__link';
    closeButton.textContent = 'Close';
    closeButton.addEventListener('click', () => this.closeAll(nav));
    closeButton.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.closeAll(nav);
      }
    });

    closeContainer.appendChild(closeButton);
    menu.appendChild(closeContainer);
  });
}

  private bindEscapeToClose(nav: HTMLElement): void {
  if(nav.dataset.accessibleNavEscapeBound === 'true') return;
  nav.dataset.accessibleNavEscapeBound = 'true';

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      this.closeAll(nav);
    }
  });
}

  private bindOutsideClickToClose(nav: HTMLElement): void {
  if(nav.dataset.accessibleNavOutsideClickBound === 'true') return;
  nav.dataset.accessibleNavOutsideClickBound = 'true';

  document.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!nav.contains(target)) {
      this.closeAll(nav);
    }
  });
}

  private toggleItem(
  item: HTMLElement,
  trigger: HTMLElement,
  toggler: HTMLButtonElement,
  submenu: HTMLElement
): void {
  const shouldOpen = !item.classList.contains('open');

  if(this.isDesktop()) {
  const siblings = item.parentElement?.querySelectorAll(':scope > li.open') || [];
  siblings.forEach((sibling) => {
    if (sibling !== item) {
      this.closeItem(sibling as HTMLElement);
    }
  });
}

if (shouldOpen) {
  item.classList.add('open');
  submenu.classList.add('open');
  submenu.style.display = 'block';
  trigger.setAttribute('aria-expanded', 'true');
  toggler.setAttribute('aria-expanded', 'true');
  return;
}

this.closeItem(item);
  }

  private closeItem(item: HTMLElement): void {
  const trigger = item.querySelector(':scope > a, :scope > .placeholder') as HTMLElement | null;
  const toggler = item.querySelector(':scope > .toggler') as HTMLButtonElement | null;
  const submenu = item.querySelector(':scope > ul') as HTMLElement | null;

  item.classList.remove('open');
  trigger?.setAttribute('aria-expanded', 'false');
    toggler?.setAttribute('aria-expanded', 'false');

    if(submenu) {
    submenu.classList.remove('open');
    submenu.style.display = '';

    const nestedOpenItems = submenu.querySelectorAll('li.open');
    nestedOpenItems.forEach((nested) => this.closeItem(nested as HTMLElement));
  }
}

  private closeAll(nav: HTMLElement): void {
  const openItems = nav.querySelectorAll('li.open');
  openItems.forEach((item) => this.closeItem(item as HTMLElement));
}

  private isDesktop(): boolean {
  return window.innerWidth >= this.desktopBreakpoint;
}
}